import { useHighlight } from '@utils/highlight-text';
import { useGeneral } from '@utils/general';
import { useWordSearchDialog } from './word-search-dialog';
import { getWordSearchData, type WordSearchPayloadItem } from './word-search-data';

const wordSearchSelector = '.word-search';
const wordSearchIgnoreSelectors = ['.heading', '.caption', '.site-page-partial', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const wordSearchHighlightSelector = '.word-search__highlight';

const { highlightQueriesInElement } = useHighlight('word-search__highlight');
const { normalizeString } = useGeneral();

type WordSearchItem = {
  description: string;
  isPrimary: boolean;
  normalizedWord: string;
  query: string;
  word: string;
};

const parseWords = (words: WordSearchPayloadItem[]): WordSearchItem[] => {
  const parsedWords = words
    .filter((word): word is WordSearchPayloadItem => typeof word === 'object' && word !== null)
    .flatMap((word) => {
      const wordValue = typeof word.word === 'string' ? word.word : '';
      const explanationValue = typeof word.explanation === 'string' ? word.explanation : '';
      const parsedWord = wordValue.trim();
      const variants = Array.isArray(word.variants)
        ? word.variants.filter((variant): variant is string => typeof variant === 'string')
        : [];
      const queries = [parsedWord, ...variants.map((variant) => variant.trim())];

      // Keep track of the source term so collisions can prefer the editor-defined primary word.
      return queries.map((query, index) => ({
        description: explanationValue.trim(),
        isPrimary: index === 0,
        normalizedWord: normalizeString(query),
        query: query,
        word: parsedWord
      }));
    })
    .filter((word): word is WordSearchItem => Boolean(word.normalizedWord));

  const wordsByNormalizedValue = new Map<string, WordSearchItem>();

  parsedWords.forEach((word) => {
    const existingWord = wordsByNormalizedValue.get(word.normalizedWord);

    // Variants can normalize to the same value as the primary term or another variant.
    // Resolve that once here so highlight lookups stay deterministic later on.
    if (!existingWord || (!existingWord.isPrimary && word.isPrimary)) {
      wordsByNormalizedValue.set(word.normalizedWord, word);
    }
  });

  return [...wordsByNormalizedValue.values()];
};

// Highlights the specified words within the given element
const highlightWordsInElement = (element: HTMLElement, words: WordSearchItem[]): void => {
  const queriesByNormalizedValue = new Map<string, string>();

  words.forEach((word) => {
    // Highlight matching also normalizes queries, so de-dupe on the normalized value here.
    if (!queriesByNormalizedValue.has(word.normalizedWord)) {
      queriesByNormalizedValue.set(word.normalizedWord, word.query);
    }
  });

  highlightQueriesInElement(
    element,
    [...queriesByNormalizedValue.values()],
    {
      ignoreSelectors: wordSearchIgnoreSelectors,
      wholeWordsOnly: true
    }
  );
};

const enhanceHighlights = (
  element: HTMLElement,
  wordsByNormalizedValue: Map<string, WordSearchItem>,
  wordSearchDialog: ReturnType<typeof useWordSearchDialog>,
  ariaLabel: string
): void => {
  const highlights = element.querySelectorAll<HTMLElement>(wordSearchHighlightSelector);

  // Iterate over each highlighted element and enhance it with accessibility attributes and event listeners
  highlights.forEach((highlight) => {
    const normalizedWord = normalizeString(highlight.textContent ?? '');
    const wordData = wordsByNormalizedValue.get(normalizedWord);

    if (!wordData || !wordData.description) {
      return;
    }

    // Add accessibility attributes to the highlight element
    highlight.setAttribute('tabindex', '0');
    highlight.setAttribute('role', 'button');
    highlight.setAttribute('aria-haspopup', 'dialog');
    highlight.setAttribute('aria-expanded', 'false');

    if (ariaLabel) {
      highlight.setAttribute('aria-label', `${ariaLabel} ${highlight.textContent}`);
    }

    // Use Word search dialog
    const toggleDialog = (): void => {
      wordSearchDialog.toggle(highlight, {
        description: wordData.description,
        title: wordData.word
      });
    };

    highlight.addEventListener('click', (event) => {
      event.preventDefault();
      toggleDialog();
    });

    highlight.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      toggleDialog();
    });
  });
};

const initWordList = (): void => {
  const { ariaLabel, words: wordSearchPayloadWords } = getWordSearchData();
  const words = parseWords(wordSearchPayloadWords);

  if (words.length === 0) {
    return;
  }

  const wordSearchDialog = useWordSearchDialog();
  const wordsByNormalizedValue = new Map(words.map((word) => [word.normalizedWord, word]));
  const elements = document.querySelectorAll<HTMLElement>(wordSearchSelector);

  // Iterate over each element matching the wordSearchSelector and apply highlights and enhancements
  elements.forEach((element) => {
    highlightWordsInElement(element, words);
    enhanceHighlights(element, wordsByNormalizedValue, wordSearchDialog, ariaLabel);
  });
};

// Initialize on DOMContentLoaded (or immediately if already loaded)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWordList, { once: true });
} else {
  initWordList();
}
